import type {
  AnnounceBannerItem,
  PublicAnnouncement,
} from "@/features/announce/api/announceClient";
import type { TypedEvent } from "@/shared/lib/componentHierarchy";

export type AnnouncementEvent =
  | {
      readonly type: "announcement/banner-opened";
      readonly banner: AnnounceBannerItem;
    }
  | {
      readonly type: "announcement/banner-dismissed";
      readonly bannerId: number;
    }
  | { readonly type: "announcement/list-retried" }
  | { readonly type: "announcement/list-page-selected"; readonly page: number }
  | { readonly type: "announcement/catalog-requested" }
  | {
      readonly type: "announcement/detail-opened";
      readonly announcement: PublicAnnouncement;
    }
  | { readonly type: "announcement/detail-retried" }
  | { readonly type: "announcement/detail-back-requested" }
  | { readonly type: "announcement/detail-not-found" }
  | { readonly type: "announcement/modal-closed" }
  | {
      readonly type: "announcement/modal-list-requested";
      readonly focusAnnouncementId?: number;
    }
  | {
      readonly type: "announcement/modal-page-requested";
      readonly announcementId?: number;
      readonly nativeNavigation?: boolean;
    }
  | {
      readonly type: "announcement/modal-initial-synced";
      readonly announcement: PublicAnnouncement | undefined;
      readonly error: unknown;
      readonly initialAnnouncementId: number | undefined;
      readonly isLoading: boolean;
      readonly listedAnnouncement: PublicAnnouncement | undefined;
    }
  | {
      readonly type: "announcement/read-requested";
      readonly announcement: Pick<
        PublicAnnouncement,
        "id" | "published_at" | "article_unread_revision"
      >;
    }
  | {
      readonly type: "announcement/read-through-requested";
      readonly publishedAt: string | null;
      readonly articleUnreadRevision: number | null;
    }
  | {
      readonly type: "announcement/current-list-read-requested";
      readonly announcements:
        | readonly Pick<
            PublicAnnouncement,
            "article_unread_revision" | "published_at"
          >[]
        | undefined;
      readonly snapshotRevision: number | null;
    }
  | {
      readonly type: "announcement/status-meta-synced";
      readonly articleTimestamp: string | null;
      readonly articleUnreadRevision: number;
      readonly isMetaReady: boolean;
      readonly canReserveMeta: boolean;
    }
  | {
      readonly type: "announcement/status-storage-synced";
      readonly readTimestamp: string | null;
      readonly readRevision: number | null;
    };

export function isAnnouncementEvent(
  event: TypedEvent,
): event is AnnouncementEvent {
  return event.type.startsWith("announcement/");
}
